import {
  afterNextRender,
  Component,
  DOCUMENT,
  ElementRef,
  HostBinding,
  HostListener,
  Inject,
  Injector,
  OnDestroy,
  OnInit,
  Renderer2,
  viewChild
} from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  NavigationEnd,
  NavigationStart,
  Router,
  RouterLink,
  RoutesRecognized
} from '@angular/router';
import { Store } from '@ngrx/store';
import { Subject, Subscription, firstValueFrom } from 'rxjs';
import { filter, pairwise, take, takeUntil } from 'rxjs/operators';
import { API_URLS } from '@core/api-url.enum';
import { ApiCallService } from '@services/api-call.service';
import { AuthService } from '@core/auth.service';
import { Application } from '@enums/application.enums';
import { CommonService } from '@modules/shared/services/common.service';
import {
  screenSizeType,
  UserData,
  WhiteLabelConfig
} from '@models/common.interface';
import * as fromUserProfile from '@modules/shared/user-profile.reducer';
import * as userProfileAction from '@modules/shared/user-profile.action';
import {
  ApplicationStatusEnums,
  PERMISSION_TYPE
} from '@shared/enums/common.enum';
import { PERMISSION_KEYS } from '@modules/shared/constants/permissions.contant';
import { FirebaseService } from '@modules/shared/services/firebase.service';
import { HEADER_LABELS } from '@constants/constant';
import { hasWhiteLabelConfig } from '@utils/common.utils';
import { APP_SHELL_CONSTANTS } from '../../../../app.constants';
import { HeaderFooterStateService } from '@modules/shared/services/header-footer-state.service';
import { WalletService } from '@wallet/service/wallet.service';
import { isReservedFrontUrlSegment } from '@constants/reserved-front-urls';
import { ROUTE_PATHS } from '@constants/routes';
import { isPublicSubPagePath } from '@modules/sub-page-view/sub-page-view.constants';
import { ApiCacheService, CacheTypes } from '@core/cache/api-cache.service';
import { MarketingPageStateService } from '@modules/shared/services/marketing-page-state.service';
import { UserNotificationService } from '@modules/shared/services/user-notification.service';
import { ChatService } from '@services/chat.service';
import { CoreService } from '@services/core.service';
import { SIDEBAR_TABS } from '@modules/shared/constants/tab-approval.constant';
import { MARKETING_PAGE_DATA } from '@constants/marketing-page-data';
import { MARKETING_PRO_PAGE_DATA } from '@constants/marketing-pro-page-data';
import { COMPARISON_PAGE_DATA } from '@constants/comparison-page-data';
import { AUTH_CONSTANTS } from '@modules/auth/constants/authConstants';
import { CREATORS_CONST } from '@modules/creators-home/constants/creatorsConstants';
import {
  PAGE_COMPARISONS,
  PAGE_FEATURES
} from '@modules/promotional/promotional/featured-categories/featured-categories.constants';
import {
  AUTH_HEADER_PATH_PREFIXES,
  getActiveHeaderLayoutData,
  HEADER_HOST_LABELS,
  isTopFanProHomePath,
  LEGAL_INFORMATIVE_PATH_PREFIXES
} from '@modules/shared/components/header/header.constants';
import type { HeaderLayoutData } from '@modules/profile/component/marketing-page-layout-container/header-layout/header-layout.interface';
import { HeaderLayoutComponent } from '@modules/profile/component/marketing-page-layout-container/header-layout/header-layout.component';
import { HeaderUserMenuComponent } from '@shared/components/header-user-menu/header-user-menu.component';
import { CreatorRequirementsModalComponent } from '@modules/edit-preference/become-a-creator/components/creator-requirements-modal/creator-requirements-modal.component';
import { DisableRightClickDirective } from '@modules/shared/directive/disable-right-click.directive';
import { MobileOptimizationDirective } from '@app-shared/directives/mobile-optimization.directive';
import { HasPermissionPipe } from '@shared/pipes/has-permission.pipe';
import { MyProfile } from '@shared/pipes/my-profile.pipe';
import { ShowTabByApprovalPipe } from '@shared/pipes/show-tab-by-approval.pipe';

@Component({
  selector: 'app-header',
  templateUrl: './header.component.html',
  styleUrls: ['./header.component.scss'],
  imports: [
    CommonModule,
    RouterLink,
    HeaderLayoutComponent,
    HeaderUserMenuComponent,
    CreatorRequirementsModalComponent,
    DisableRightClickDirective,
    MobileOptimizationDirective,
    HasPermissionPipe,
    MyProfile,
    ShowTabByApprovalPipe
  ]
})
export class HeaderComponent implements OnInit, OnDestroy {
  isHeaderVisible = true;
  isHeaderOpen = false;
  isWhiteLabelDataLoading = false;
  isWhitelabel = false;
  isMarketingPageVisible = false;
  isAtHomePage = false;
  isLegalInformativePage = false;
  isAtDashboardPage = false;
  isAtProfileByUrl = false;
  isAtProfile = false;
  isHidden = false;
  isAdminAccess = false;
  backgroundColor: string | null = null;
  themeColor: string | null = null;
  backBtnDetails = null;
  profilePageUser = null;
  whitelabelUserData;
  whiteLabelConfig: WhiteLabelConfig;
  userProfileData = {} as any;
  headerLayoutData: HeaderLayoutData = getActiveHeaderLayoutData({
    isWhiteLabel: false
  });
  headerLabels = HEADER_LABELS;
  messageCount = '';
  readonly sidebarTabs = SIDEBAR_TABS;
  readonly permissionKeys = PERMISSION_KEYS;
  readonly PERMISSION_TYPE = PERMISSION_TYPE;
  headerHostLabels = HEADER_HOST_LABELS;
  AUTH_CONSTANTS = AUTH_CONSTANTS;
  CREATORS_CONST = CREATORS_CONST;
  PAGE_FEATURES = PAGE_FEATURES;
  PAGE_COMPARISONS = PAGE_COMPARISONS;
  isFeaturesDropdownOpen = false;
  isCompareDropdownOpen = false;
  isMarketingKebabMenuOpen = false;
  isAtCreatorHomePage = false;
  isPromotionalHome = false;
  isAtProHomePage = false;
  isAtProPage = false;
  isAtAuthPath = false;
  readonly creatorsRequirementModal = viewChild<any>('creatorsRequirementModal');
  scrSize = 1220;
  top: number;
  screenSize: screenSizeType = {
    desktop: Application.NINE_HUNDRED_NINETY_TWO as number,
    mobile: Application.NINE_HUNDRED_NINETY_TWO as number
  };

  private readonly destroy$ = new Subject<void>();
  private readonly subscriptionList: Subscription[] = [];
  private readonly acs: ApiCallService;
  public readonly cs: CommonService;
  public authService: AuthService;
  private readonly chatService: ChatService;
  private readonly coreService: CoreService;
  applicationStatusEnums = ApplicationStatusEnums;

  constructor(
    private readonly router: Router,
    @Inject(DOCUMENT) private readonly document: Document,
    private readonly store: Store<fromUserProfile.UserProfileState>,
    _injector: Injector,
    private readonly firebaseService: FirebaseService,
    private readonly headerFooterStateService: HeaderFooterStateService,
    private readonly commonService: CommonService,
    private readonly walletService: WalletService,
    private readonly apiCacheService: ApiCacheService,
    private readonly marketingPageStateService: MarketingPageStateService,
    private readonly userNotificationService: UserNotificationService,
    private readonly renderer: Renderer2,
    private readonly el: ElementRef
  ) {
    this.cs = _injector.get<CommonService>(CommonService);
    this.acs = _injector.get<ApiCallService>(ApiCallService);
    this.authService = _injector.get<AuthService>(AuthService);
    this.chatService = _injector.get<ChatService>(ChatService);
    this.coreService = _injector.get<CoreService>(CoreService);
    if (this.cs.isBrowser) {
      this.scrSize = window.innerWidth;
      window.onblur = () => {
        this.isHidden = true;
      };
      window.onfocus = () => {
        this.isHidden = false;
      };
      this.routerState();
    }
    this.runInitialFunctions();
    this.isHeaderVisible =
      this.headerFooterStateService.stateSubject.value.isHeaderVisible;
    afterNextRender(() => {
      this.syncStoredSessionChrome();
    });
  }

  @HostListener('window:resize', ['$event'])
  onResize(event: Event): void {
    if (this.cs.isBrowser) {
      this.scrSize = (event.target as Window).innerWidth;
    }
  }

  @HostBinding('style.--custom-theme-color')
  get cssVariable(): string | null {
    return this.themeColor;
  }

  @HostBinding('style.--custom-background-color')
  get cssBgVariable(): string | null {
    return this.backgroundColor;
  }

  get isMobileViewport(): boolean {
    return this.scrSize <= this.screenSize.mobile;
  }

  get showWhiteLabelMobileChrome(): boolean {
    return this.isWhitelabel && this.isMobileViewport;
  }

  get isAtWLSHomePage(): boolean {
    const path = this.router.url?.split('?')[0]?.split('#')[0] ?? '';
    return path === '/' && hasWhiteLabelConfig();
  }

  get headerLayoutPinTop(): string {
    return this.top ? `${this.top}px` : '0px';
  }

  get showTopFanMarketingNav(): boolean {
    if (
      this.cs.isBrowser &&
      !this.router.navigated &&
      !this.authService.hasStoredSession()
    ) {
      return false;
    }
    if (this.authService.hasStoredSession() && !this.isMarketingPath) {
      return false;
    }
    return (
      !this.isWhitelabel &&
      !this.authService.isWhiteLabelWebsite() &&
      this.isMarketingPath
    );
  }

  get showMarketingGuestCtas(): boolean {
    return (
      this.showTopFanMarketingNav &&
      !this.authService.hasStoredSession() &&
      !this.authService.isAuthenticated()
    );
  }

  get showCreatorsCta(): boolean {
    return (
      this.showMarketingGuestCtas &&
      (this.isCurrentHomePage || this.isCurrentLegalPage)
    );
  }

  get showMarketingPageCtas(): boolean {
    return (
      this.showTopFanMarketingNav &&
      (this.isMarketingPageVisible ||
        this.isCurrentCreatorHomePage ||
        this.isAtProPage)
    );
  }

  get showWhiteLabelMobileGetStarted(): boolean {
    return (
      (this.isWhitelabel || this.authService.isWhiteLabelWebsite()) &&
      !this.isCurrentAuthPath
    );
  }

  get hideHeaderGuestAuthCtas(): boolean {
    if (this.cs.isBrowser && !this.router.navigated) {
      return true;
    }
    if (
      this.authService.hasStoredSession() ||
      this.authService.isAuthenticated()
    ) {
      return true;
    }
    if (this.isCurrentAuthPath) {
      return true;
    }
    if (this.showsLeftGuestSignIn) {
      return true;
    }
    return !this.isWhitelabel && !this.authService.isWhiteLabelWebsite();
  }

  private get showsLeftGuestSignIn(): boolean {
    if (
      this.authService.isLogin ||
      !this.cs.updateSignInVisibility() ||
      this.isMarketingPageVisible
    ) {
      return false;
    }
    const path = this.currentPath;
    const extraPaths = this.cs.isWhiteLabelWeb
      ? []
      : APP_SHELL_CONSTANTS.TOPFANS_ONLY_HIDDEN_SIDEBAR_PATHS;
    const sidebarHidden =
      [...APP_SHELL_CONSTANTS.HIDDEN_SIDEBAR_PATHS, ...extraPaths].some((route) => {
        const normalizedRoute = route.endsWith('/') ? route.slice(0, -1) : route;
        return path === normalizedRoute || path.startsWith(`${normalizedRoute}/`);
      }) ||
      (path === ROUTE_PATHS.HOME_PATH && !hasWhiteLabelConfig());
    return !sidebarHidden;
  }

  private get currentPath(): string {
    if (this.cs.isBrowser) {
      return window.location.pathname || '/';
    }
    return (this.router.url ?? '').split('?')[0].split('#')[0];
  }

  private get isMarketingPath(): boolean {
    return (
      this.isCurrentHomePage ||
      this.isCurrentLegalPage ||
      this.isCurrentCreatorHomePage ||
      this.isPromotionalHome ||
      this.isMarketingPageVisible ||
      this.isAtProPage ||
      this.isCurrentProHomePage
    );
  }

  private get isCurrentHomePage(): boolean {
    return this.currentPath === '/' && !hasWhiteLabelConfig();
  }

  private get isCurrentLegalPage(): boolean {
    return LEGAL_INFORMATIVE_PATH_PREFIXES.some(
      (prefix) =>
        this.currentPath === prefix || this.currentPath.startsWith(`${prefix}/`)
    );
  }

  private get isCurrentCreatorHomePage(): boolean {
    return this.currentPath === '/creators' || this.currentPath === '/creator';
  }

  private get isCurrentProHomePage(): boolean {
    return isTopFanProHomePath(this.currentPath);
  }

  private get isCurrentAuthPath(): boolean {
    return AUTH_HEADER_PATH_PREFIXES.some(
      (prefix) =>
        this.currentPath === prefix || this.currentPath.startsWith(`${prefix}/`)
    );
  }

  ngOnInit(): void {
    this.syncStoredSessionChrome();
    this.updateRouteBasedFlags();

    if (!this.cs.isBrowser) {
      this.router.events
        .pipe(
          filter((event) => event instanceof NavigationEnd),
          take(1),
          takeUntil(this.destroy$)
        )
        .subscribe(() => {
          this.updateRouteBasedFlags();
          this.syncHeaderLayoutData();
        });
    }

    this.headerFooterStateService.stateSubject
      .pipe(takeUntil(this.destroy$))
      .subscribe((state) => {
        this.backgroundColor = state.backgroundColor;
        this.themeColor = state.themeColor;
        this.isHeaderVisible = state.isHeaderVisible;
        this.backBtnDetails = state.backBtnDetails;
        this.isAtProfile = state.isAtProfile;
        this.profilePageUser = state.profilePageUser;
        this.updateRouteBasedFlags();
        this.syncHeaderLayoutData();
        if (state.reTriggerNeeded) {
          this.runInitialFunctions();
        }
        if (state.top) {
          this.top = state.top;
        }
      });

    this.isWhiteLabelDataLoading =
      !!this.commonService.isWhiteLabelWeb && !this.cs.isWhiteLabelWeb;

    this.coreService.messageCount
      .pipe(takeUntil(this.destroy$))
      .subscribe((count: number) => {
        this.messageCount = count ? this.cs.kFormatter(count) : '';
      });

    this.walletService.walletCountUpdate
      .pipe(takeUntil(this.destroy$))
      .subscribe((res) => {
        if (res) {
          this.userNotificationService.refresh();
        }
      });

    this.marketingPageStateService.isMarketingPageVisible$
      .pipe(takeUntil(this.destroy$))
      .subscribe((visible) => {
        this.isMarketingPageVisible = visible;
        this.syncHeaderLayoutData();
      });
    this.marketingPageStateService.isPromotionalHome$
      .pipe(takeUntil(this.destroy$))
      .subscribe((visible) => {
        this.isPromotionalHome = visible;
        this.syncHeaderLayoutData();
      });
    this.marketingPageStateService.isMarketingProPage$
      .pipe(takeUntil(this.destroy$))
      .subscribe((isProPage) => {
        this.isAtProPage = isProPage;
        this.syncHeaderLayoutData();
      });

    this.loadMarketingListingPages();
    this.loadMarketingProListingPages();
    this.loadComparisonListingPages();
  }

  ngOnDestroy(): void {
    this.top = null;
    this.closeMarketingMenus();
    this.subscriptionList.forEach((sub) => sub?.unsubscribe());
    this.destroy$.next();
    this.destroy$.complete();
  }

  onHeaderOpen(isOpened: boolean): void {
    this.isHeaderOpen = isOpened;
  }

  navigateToLiveStream(): void {
    if (
      this.userProfileData?.is_live &&
      this.userProfileData?.livestream_id &&
      this.userProfileData?.user_name
    ) {
      this.router.navigate([
        `dashboard/live-stream/${this.userProfileData.user_name}/${this.userProfileData.livestream_id}`
      ]);
      return;
    }
    this.router.navigate(['/dashboard/live-stream']);
  }

  async onRoute(): Promise<void> {
    if (this.authService.isUnAuthorizedUser()) {
      this.router.navigateByUrl(ROUTE_PATHS.CONTENT_PATH);
      return;
    }
    if (!this.authService.isAuthenticated()) {
      this.router.navigateByUrl(ROUTE_PATHS.HOME_PATH);
      return;
    }
    const raw = await firstValueFrom(
      this.store.select(fromUserProfile.getUserProfileItems).pipe(take(1))
    );
    const res =
      raw && !Array.isArray(raw) && typeof raw === 'object'
        ? (raw as UserData)
        : null;
    if (res?.admin_approval_status === this.applicationStatusEnums.PENDING) {
      this.router.navigateByUrl(ROUTE_PATHS.EDIT_PREFERENCE_PATH);
      return;
    }
    if (hasWhiteLabelConfig()) {
      this.cs.navigateToWhiteLabelHome();
      return;
    }
    if (!res) {
      this.router.navigateByUrl(ROUTE_PATHS.DASHBOARD_PATH);
      return;
    }
    const creatorOrApproved =
      !!res.is_creator ||
      res.admin_approval_status === this.applicationStatusEnums.APPROVED;
    if (creatorOrApproved && res.user_name) {
      if (res.is_creator) {
        this.router.navigate([ROUTE_PATHS.EDIT_PREFERENCE_WHITELABEL]);
      } else {
        this.router.navigate([res.user_name]);
      }
      return;
    }
    this.router.navigateByUrl(ROUTE_PATHS.DASHBOARD_PATH);
  }

  private syncHeaderLayoutData(): void {
    this.headerLayoutData = getActiveHeaderLayoutData({
      isWhiteLabel: this.isWhitelabel || this.authService.isWhiteLabelWebsite(),
      isAtProHomePage: this.isAtProHomePage,
      isPromotionalHome: this.isPromotionalHome,
      isAtHomePage: this.isAtHomePage,
      whiteLabelLogoUrl: this.resolveWhiteLabelLogoUrl()
    });
  }

  private resolveWhiteLabelLogoUrl(): string {
    const config = this.whiteLabelConfig as
      | (WhiteLabelConfig & {
          custom_logo?: Record<string, { url?: string } | undefined>;
        })
      | undefined;
    const logoUrl = String(config?.logo_url ?? '').trim();
    if (logoUrl) {
      return logoUrl;
    }
    const customLogo =
      config?.custom_logo?.['144'] ??
      Object.values(config?.custom_logo ?? {}).find((logo) =>
        String(logo?.url ?? '').trim()
      );
    return String(customLogo?.url ?? '').trim();
  }

  private syncStoredSessionChrome(): void {
    if (!this.cs.isBrowser) {
      return;
    }
    const root = this.document.documentElement;
    if (!this.authService.hasStoredSession()) {
      root.classList.remove('tf-has-session', 'tf-session-lock');
      return;
    }
    root.classList.add('tf-has-session', 'tf-session-lock');
  }

  private updateRouteBasedFlags(): void {
    const path = this.router.url?.split('?')?.[0] ?? '';
    this.isAtHomePage = path === '/' && !hasWhiteLabelConfig();
    this.isAtCreatorHomePage = path === '/creators' || path === '/creator';
    this.isAtProHomePage = isTopFanProHomePath(path);
    this.isAtAuthPath = AUTH_HEADER_PATH_PREFIXES.some(
      (prefix) => path === prefix || path.startsWith(`${prefix}/`)
    );
    this.isLegalInformativePage = LEGAL_INFORMATIVE_PATH_PREFIXES.some(
      (prefix) => path === prefix || path.startsWith(`${prefix}/`)
    );
    this.isAtDashboardPage = path.startsWith('/dashboard');
    const segments = path.split('/').filter(Boolean);
    this.isAtProfileByUrl =
      segments.length === Application.ONE &&
      !isReservedFrontUrlSegment(segments[0] ?? '') &&
      !isPublicSubPagePath(path);
  }

  private runInitialFunctions(): void {
    if (this.authService.isAuthenticated()) {
      this.userProfile();
      this.getFollowers();
      if (!this.isHidden) {
        this.userNotificationService.refresh();
      }
    }
    this.updateRouteBasedFlags();
    this.isWhitelabel = this.authService.isWhiteLabelWebsite();
    this.syncHeaderLayoutData();
    if (this.cs.isBrowser) {
      this.isAdminAccess = !!this.authService.userContext?.fm_user;
      if (this.isAdminAccess || this.authService.isWhiteLabelPreview) {
        this.document.body.classList.add('admin-access');
      }
      if (this.authService.isAuthenticated()) {
        this.userNotificationService.start();
      }
      this.applyWhiteLabelConfiguration(this.cs.getWhiteLabelConfiguration());
      const whiteLabelSub = this.cs
        .getWhiteLabelConfiguration$()
        .subscribe((config) => {
          this.applyWhiteLabelConfiguration(config);
        });
      this.subscriptionList.push(whiteLabelSub);
    } else {
      this.applyWhiteLabelConfiguration(this.cs.getWhiteLabelConfiguration());
    }
  }

  private applyWhiteLabelConfiguration(config: any): void {
    if (!config?.data?.whiteLabel) {
      return;
    }
    this.whitelabelUserData = config.user;
    this.whiteLabelConfig = config.data.whiteLabel;
    this.isWhiteLabelDataLoading = false;
    this.syncHeaderLayoutData();
  }

  private routerState(): void {
    this.router.events
      .pipe(
        filter((evt: any) => evt instanceof RoutesRecognized),
        pairwise(),
        takeUntil(this.destroy$)
      )
      .subscribe((events: RoutesRecognized[]) => {
        if (
          events[Application.ONE].urlAfterRedirects.includes(
            this.userProfileData?.user_name
          )
        ) {
          if (
            !this.authService.getHistory()?.includes('content') &&
            this.authService.getHistory() &&
            !events[Application.ZERO].urlAfterRedirects?.includes('share')
          ) {
            this.authService.setLocationHistory(
              events[Application.ZERO].urlAfterRedirects
            );
          }
        }
        if (
          events[Application.ZERO].urlAfterRedirects.includes('messages') ||
          events[Application.ZERO].urlAfterRedirects.includes('resources')
        ) {
          this.authService.setLocationHistory(
            events[Application.ZERO].urlAfterRedirects
          );
        }
      });

    this.router.events.pipe(takeUntil(this.destroy$)).subscribe((event) => {
      if (event instanceof NavigationStart || event instanceof NavigationEnd) {
        if (event instanceof NavigationEnd) {
          this.userProfile();
        }
        this.closeMarketingMenus();
        this.updateRouteBasedFlags();
        this.syncStoredSessionChrome();
        this.syncHeaderLayoutData();
      }
    });
  }

  private getFollowers(): void {
    if (!this.authService.isAuthenticated()) {
      return;
    }
    this.acs.get(API_URLS.getSavedAndFollower, false).subscribe({
      next: (res) => {
        if (res.status) {
          this.store.dispatch(new userProfileAction.SetFollowers(res.data));
        } else {
          this.cs.showErrorMessage(res.message);
        }
      },
      error: (err) => {
        this.cs.showErrorMessage(err.message);
      }
    });
  }

  private userProfile(): void {
    if (!this.authService.isAuthenticated()) {
      return;
    }
    this.acs
      .get(
        `${API_URLS.users}?host_name=${this.cs.isBrowser ? window.location.origin : null}`,
        false
      )
      .subscribe({
        next: (res) => {
          if (res.status) {
            if (res.is_suspended) {
              this.authService.removeDetails();
              this.router.navigate(['auth/login']);
              return;
            }
            const mergeObj = {
              ...res.creator_detail,
              ...res.data,
              is_white_label: res.is_white_label,
              is_host_owner: res.is_host_owner,
              delegate_user: res.delegate_user
            };
            this.userProfileData = mergeObj;
            this.userProfileData.isAbove18 = this.cs.isUser18Above(
              this.userProfileData.birth_date
            );
            this.authService.userData = mergeObj;
            this.authService.setIsCreator(!!mergeObj.is_creator);
            this.headerFooterStateService.emitUserData(mergeObj);
            this.store.dispatch(new userProfileAction.SetUserProfile(mergeObj));
            const savedTheme = this.cs.getValueFromLocalStorage('theme');
            const theme = savedTheme ? savedTheme : Application.THEME_DARK;
            if (!this.userProfileData?.is_white_label) {
              this.updateProfileTheme(res.data.profile_theme || theme);
            }
            this.setUserOnline();
            if (!this.isAdminAccess) {
              this.firebaseService.requestFirebaseToken();
            }
          } else {
            this.updateProfileTheme(Application.THEME_DARK);
            this.cs.showErrorMessage(res.message);
          }
        },
        error: (err) => {
          this.updateProfileTheme(Application.THEME_DARK);
          if (err?.message !== Application.UNAUTHORIZED) {
            this.cs.showErrorMessage(err.message);
          }
        }
      });
  }

  private updateProfileTheme(selectedTheme: string): void {
    if (this.isWhitelabel) {
      return;
    }
    this.cs.updateTheme(selectedTheme);
  }

  private setUserOnline(): void {
    if (this.userProfileData?.email) {
      this.chatService.makeConnection();
      this.chatService.connect();
      this.chatService.setChatEmail(
        this.userProfileData?.email,
        this.userProfileData?.gsi4pk
      );
    }
  }

  private loadMarketingListingPages(): void {
    const prefetchedData = MARKETING_PAGE_DATA['home'];
    if (prefetchedData) {
      this.processMarketingListingData(prefetchedData);
      return;
    }
    this.apiCacheService
      .get(
        `${API_URLS.marketingPage}?url=home&include_sections=true`,
        CacheTypes.PRERENDERED
      )
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res: any) => {
          this.processMarketingListingData(res);
        }
      });
  }

  private processMarketingListingData(res: any): void {
    if (!res.data?.sections?.length) {
      return;
    }
    const featuresSection = res.data.sections.find(
      (section) => section.layout_id === 'vertical-card-carousel-layout'
    );
    const verticalCards = featuresSection?.vertical_cards;
    if (!verticalCards?.length) {
      return;
    }
    this.cs.marketingPagesRoutes = verticalCards
      .filter((card: { redirection_link: string }) => card.redirection_link)
      .map((card: { redirection_link: string }) => card.redirection_link);
    this.PAGE_FEATURES = verticalCards.map((card) => ({
      imageUrl: card.image,
      title: card.heading,
      redirectUrl: card.redirection_link
    }));
  }

  private loadMarketingProListingPages(): void {
    if (Object.keys(MARKETING_PRO_PAGE_DATA).length > 0) {
      this.processMarketingProListingData(MARKETING_PRO_PAGE_DATA);
      return;
    }
    this.apiCacheService
      .get(
        `${API_URLS.marketingPage}?limit=50&type=MARKETING_PRO`,
        CacheTypes.PRERENDERED
      )
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res: any) => {
          this.processMarketingProListingData(res);
        }
      });
  }

  private processMarketingProListingData(source: any): void {
    const routes = this.resolveMarketingProRoutes(source);
    if (routes.length) {
      this.cs.marketingProPagesRoutes = routes;
    }
  }

  private resolveMarketingProRoutes(source: any): string[] {
    if (source && typeof source === 'object' && !Array.isArray(source?.data)) {
      return Object.values(source)
        .filter((entry: any) => entry?.data?.url)
        .map((entry: any) => `/${entry.data.url.replace(/\/$/, '')}`);
    }
    if (source?.data && Array.isArray(source.data)) {
      return source.data
        .filter((page: any) => page?.url)
        .map((page: any) => `/${page.url.replace(/\/$/, '')}`);
    }
    return [];
  }

  navigateToCreators(): void {
    this.closeMarketingKebabMenu();
    this.router.navigate(['/creators']);
    this.isAtCreatorHomePage = true;
  }

  navigateToRequestADemo(): void {
    this.closeMarketingKebabMenu();
    this.router.navigate(['/' + ROUTE_PATHS.REQUEST_DEMO]);
  }

  applyToJoin(): void {
    this.closeMarketingKebabMenu();
    this.creatorsRequirementModal()?.openModal();
  }

  toggleFeaturesDropdown(event?: Event): void {
    event?.stopPropagation();
    this.isFeaturesDropdownOpen = !this.isFeaturesDropdownOpen;
    if (this.isFeaturesDropdownOpen) {
      this.isCompareDropdownOpen = false;
    }
    this.isMarketingKebabMenuOpen = false;
    this.updateBodyScroll();
  }

  closeFeaturesDropdown(clickedItem: any): void {
    this.isFeaturesDropdownOpen = false;
    this.updateBodyScroll();
    if (clickedItem?.redirectUrl) {
      this.router.navigate([`/${clickedItem.redirectUrl}`]);
    }
  }

  toggleCompareDropdown(event?: Event): void {
    event?.stopPropagation();
    this.isCompareDropdownOpen = !this.isCompareDropdownOpen;
    if (this.isCompareDropdownOpen) {
      this.isFeaturesDropdownOpen = false;
    }
    this.isMarketingKebabMenuOpen = false;
    this.updateBodyScroll();
  }

  closeCompareDropdown(clickedItem: any): void {
    this.isCompareDropdownOpen = false;
    this.updateBodyScroll();
    if (clickedItem?.redirectUrl) {
      this.router.navigate([`/${clickedItem.redirectUrl}`]);
    }
  }

  toggleMarketingKebabMenu(event: Event): void {
    event.stopPropagation();
    this.isMarketingKebabMenuOpen = !this.isMarketingKebabMenuOpen;
    if (this.isMarketingKebabMenuOpen) {
      this.isFeaturesDropdownOpen = false;
      this.isCompareDropdownOpen = false;
    }
    this.updateBodyScroll();
  }

  closeMarketingKebabMenu(): void {
    this.isMarketingKebabMenuOpen = false;
    this.updateBodyScroll();
  }

  openFeaturesFromKebab(event: Event): void {
    event.stopPropagation();
    this.isMarketingKebabMenuOpen = false;
    this.isCompareDropdownOpen = false;
    this.isFeaturesDropdownOpen = true;
    this.updateBodyScroll();
  }

  openCompareFromKebab(event: Event): void {
    event.stopPropagation();
    this.isMarketingKebabMenuOpen = false;
    this.isFeaturesDropdownOpen = false;
    this.isCompareDropdownOpen = true;
    this.updateBodyScroll();
  }

  @HostListener('document:click', ['$event'])
  hideMarketingMenus(event: Event): void {
    if (!this.cs.isBrowser) {
      return;
    }
    const target = event.target as HTMLElement;
    const isKebab = !!target?.closest?.('.marketing-kebab-wrapper');
    const isDropdown = !!target?.closest?.(
      '.features-toggle-btn, .marketing-kebab-menu__item, .features-dropdown-panel'
    );
    if (!isKebab) {
      this.isMarketingKebabMenuOpen = false;
    }
    if (!isDropdown && !this.el.nativeElement.contains(event.target)) {
      this.isFeaturesDropdownOpen = false;
      this.isCompareDropdownOpen = false;
    }
    this.updateBodyScroll();
  }

  private closeMarketingMenus(): void {
    this.isFeaturesDropdownOpen = false;
    this.isCompareDropdownOpen = false;
    this.isMarketingKebabMenuOpen = false;
    this.updateBodyScroll();
  }

  private updateBodyScroll(): void {
    if (!this.cs.isBrowser) {
      return;
    }
    const anyOpen =
      this.isFeaturesDropdownOpen ||
      this.isCompareDropdownOpen ||
      this.isMarketingKebabMenuOpen;
    if (anyOpen) {
      this.renderer.addClass(
        this.document.documentElement,
        'features-dropdown-open'
      );
    } else {
      this.renderer.removeClass(
        this.document.documentElement,
        'features-dropdown-open'
      );
    }
  }

  private loadComparisonListingPages(): void {
    if (Object.keys(COMPARISON_PAGE_DATA).length > 0) {
      this.processComparisonListingData(COMPARISON_PAGE_DATA);
      return;
    }
    this.apiCacheService
      .get(
        `${API_URLS.marketingPage}?limit=50&type=MARKETING_COMPARISON`,
        CacheTypes.PRERENDERED
      )
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res: any) => {
          this.processComparisonListingData(res);
        }
      });
  }

  private processComparisonListingData(source: any): void {
    const comparisons = this.resolveComparisonListingItems(source);
    if (comparisons.length) {
      this.PAGE_COMPARISONS = comparisons;
    }
  }

  private resolveComparisonListingItems(
    source: any
  ): Array<{ competitor_logo: string; redirectUrl: string }> {
    if (source && typeof source === 'object' && !Array.isArray(source?.data)) {
      return Object.values(source)
        .filter((entry: any) => entry?.data?.url)
        .sort(
          (a: any, b: any) =>
            (a?.data?.sequence_number ?? Application.ZERO) -
            (b?.data?.sequence_number ?? Application.ZERO)
        )
        .map((entry: any) => this.mapComparisonPageToListingItem(entry.data))
        .filter(
          (item): item is { competitor_logo: string; redirectUrl: string } =>
            item !== null
        );
    }
    if (Array.isArray(source?.data)) {
      return [...source.data]
        .sort(
          (a: any, b: any) =>
            (a?.sequence_number ?? Application.ZERO) -
            (b?.sequence_number ?? Application.ZERO)
        )
        .map((page: any) => this.mapComparisonPageToListingItem(page))
        .filter(
          (item): item is { competitor_logo: string; redirectUrl: string } =>
            item !== null
        );
    }
    return [];
  }

  private mapComparisonPageToListingItem(
    page: any
  ): { competitor_logo: string; redirectUrl: string } | null {
    const redirectUrl = page?.url?.trim();
    const competitorLogo = page?.competitor_logo?.trim();
    if (
      !redirectUrl ||
      !competitorLogo ||
      page?.status?.toUpperCase() !== Application.PUBLISHED?.toUpperCase()
    ) {
      return null;
    }
    return {
      competitor_logo: competitorLogo,
      redirectUrl
    };
  }
}
